import { z } from "zod";

/**
 * Wire mirrors of shore's `employee-advance.dto.ts` (HRMS-EXP-010 section 6).
 *
 * Mirrored from the SHIPPED shore file. Three things in it are decisions
 * rather than details, and the UI is built on them:
 *
 * 1. THERE IS NO DRAFT. An advance lands `pending` with a live approval
 *    request in the same transaction, so there is no save-and-submit pair and
 *    no `submit` flag. The request dialog therefore commits once.
 * 2. MARK-PAID CARRIES NO AMOUNT. Disbursement is one-shot and full: an admin
 *    who wants to hand over less asks for a smaller advance. So that dialog is
 *    a confirmation, not a form.
 * 3. `spendableBalance` IS DERIVED AND SHIPPED, never re-derived here, so the
 *    figure on screen cannot drift from the one HRMS-EXP-011 will spend
 *    against.
 */

const MONEY = /^\d{1,13}(\.\d{1,2})?$/;

/// The eight statuses shore whitelists. Kept as a list rather than a loose
/// string so the chips and the filter share one vocabulary; the row itself is
/// typed as a plain string below, so a value this app has not heard of renders
/// as itself rather than blanking the cell.
export const ADVANCE_STATUSES = [
  "pending",
  "approved",
  "rejected",
  "cancelled",
  "paid",
  "claimed",
  "returned",
  "partly_claimed_and_returned",
] as const;
export type AdvanceStatus = (typeof ADVANCE_STATUSES)[number];

export const ADVANCE_SCOPES = ["mine", "team", "workspace"] as const;
export type AdvanceScope = (typeof ADVANCE_SCOPES)[number];

export const employeeAdvanceRowSchema = z
  .object({
    id: z.string(),
    membershipId: z.string(),
    memberName: z.string(),
    purpose: z.string(),
    currency: z.string(),
    amount: z.string(),
    paidAmount: z.string(),
    claimedAmount: z.string(),
    returnedAmount: z.string(),
    /// paidAmount minus claimedAmount minus returnedAmount, derived by shore.
    spendableBalance: z.string(),
    status: z.string(),
    createdAt: z.string(),
    version: z.number().int(),
  })
  .strict();
export type EmployeeAdvanceRow = z.infer<typeof employeeAdvanceRowSchema>;

export const employeeAdvanceListSchema = z
  .object({
    rows: z.array(employeeAdvanceRowSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();
export type EmployeeAdvanceList = z.infer<typeof employeeAdvanceListSchema>;

export const createEmployeeAdvanceSchema = z
  .object({
    purpose: z.string().trim().min(1, "Say what the advance is for.").max(200),
    currency: z.string().regex(/^[A-Z]{3}$/, "Use a 3-letter currency code."),
    amount: z.string().regex(MONEY, "Use an amount with at most 2 decimal places."),
  })
  .strict();
export type CreateEmployeeAdvance = z.infer<typeof createEmployeeAdvanceSchema>;

/// `reason` is REQUIRED here where a claim's cancel leaves it optional. A
/// claim can be cancelled while it is still a private draft; an advance is
/// submitted the moment it exists, so a cancel always withdraws a live request
/// from somebody's inbox and that person is owed the reason.
export const cancelEmployeeAdvanceSchema = z
  .object({
    version: z.number().int().min(0),
    reason: z.string().trim().min(3, "Say why you are withdrawing this.").max(500),
  })
  .strict();
export type CancelEmployeeAdvance = z.infer<typeof cancelEmployeeAdvanceSchema>;

export const markAdvancePaidSchema = z.object({ version: z.number().int().min(0) }).strict();
export type MarkAdvancePaid = z.infer<typeof markAdvancePaidSchema>;

/// `amount` is what came back on THIS occasion, never a running total: two
/// returns are two calls, for the reason the claim's mark-paid gives.
export const recordAdvanceReturnSchema = z
  .object({
    version: z.number().int().min(0),
    amount: z.string().regex(MONEY, "Use an amount with at most 2 decimal places."),
  })
  .strict();
export type RecordAdvanceReturn = z.infer<typeof recordAdvanceReturnSchema>;

export const listEmployeeAdvancesQuerySchema = z
  .object({
    scope: z.enum(ADVANCE_SCOPES).optional(),
    status: z.enum(ADVANCE_STATUSES).optional(),
    membershipId: z.string().optional(),
    cursor: z.string().optional(),
    limit: z.number().int().min(1).max(100).optional(),
  })
  .strict();
export type ListEmployeeAdvancesQuery = z.infer<typeof listEmployeeAdvancesQuerySchema>;
